'use client';

import { useCallback, useEffect, useState } from 'react';

const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY!;
const BUILD_VERSION = process.env.NEXT_PUBLIC_BUILD_VERSION ?? 'dev';
const SERVICE_WORKER_URL = `/sw.js?v=${encodeURIComponent(BUILD_VERSION)}`;

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer as ArrayBuffer;
}

async function subscribeToPush(registration: ServiceWorkerRegistration) {
  try {
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      console.log('[PWA] Push subscription created');
    }

    // Siempre reenviar al backend para asociar/actualizar el userId
    // según la sesión activa (cookie session_token).
    await fetch('/api/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subscription),
      credentials: 'include',
    });

    return subscription;
  } catch (err) {
    console.warn('[PWA] Push subscription failed:', err);
    return null;
  }
}

async function requestNotificationPermission(registration: ServiceWorkerRegistration) {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    await subscribeToPush(registration);
    return;
  }
  if (Notification.permission === 'denied') return;

  // Pequeña espera para no bombardear al usuario al cargar la página.
  await new Promise((r) => setTimeout(r, 3000));

  const permission = await Notification.requestPermission();
  if (permission === 'granted') {
    await subscribeToPush(registration);
  }
}

/** Hook para que un componente UI pueda solicitar permisos por gesto del usuario. */
export function useRequestPushPermission() {
  return useCallback(async (): Promise<NotificationPermission | 'unsupported'> => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    if (!('serviceWorker' in navigator)) return 'unsupported';
    if (Notification.permission === 'granted') {
      const reg = await navigator.serviceWorker.ready;
      await subscribeToPush(reg);
      return 'granted';
    }
    if (Notification.permission === 'denied') return 'denied';
    const result = await Notification.requestPermission();
    if (result === 'granted') {
      const reg = await navigator.serviceWorker.ready;
      await subscribeToPush(reg);
    }
    return result;
  }, []);
}

// ── Global store for the deferred install prompt ──────────────
type DeferredPrompt = Event & { prompt: () => void; userChoice: Promise<{ outcome: string }> };

let globalDeferredPrompt: DeferredPrompt | null = null;
const installListeners = new Set<(available: boolean) => void>();

function notifyInstallListeners(available: boolean) {
  installListeners.forEach((fn) => fn(available));
}

async function clearManagedCaches() {
  if (typeof window === 'undefined' || !('caches' in window)) return;

  const keys = await caches.keys();
  await Promise.all(
    keys
      .filter((key) => key.startsWith('live-moto-'))
      .map((key) => caches.delete(key))
  );
}

/** Hook to get the PWA install state and trigger the prompt */
export function usePWAInstall() {
  const [canInstall, setCanInstall] = useState(() => !!globalDeferredPrompt);

  useEffect(() => {
    const listener = (available: boolean) => setCanInstall(available);
    installListeners.add(listener);
    return () => { installListeners.delete(listener); };
  }, []);

  const install = useCallback(async () => {
    if (!globalDeferredPrompt) return;
    globalDeferredPrompt.prompt();
    const { outcome } = await globalDeferredPrompt.userChoice;
    console.log('[PWA] Install outcome:', outcome);
    if (outcome === 'accepted') {
      globalDeferredPrompt = null;
      notifyInstallListeners(false);
    }
  }, []);

  return { canInstall, install };
}

/** Drop-in component — registers SW and captures the install prompt */
export function PWARegistration() {
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    // Si la app está corriendo en modo standalone (ya instalada como PWA), avisar al backend.
    try {
      const isStandalone =
        window.matchMedia?.('(display-mode: standalone)').matches ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (navigator as any).standalone === true;
      if (isStandalone) {
        fetch('/api/download/installed', { method: 'POST' }).catch(() => {});
      }
    } catch { /* noop */ }

    // In development we disable SW to avoid stale cached JS/CSS while iterating UI fixes.
    if (process.env.NODE_ENV !== 'production') {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => registration.unregister());
      }).catch(() => {});

      if ('caches' in window) {
        caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key)))).catch(() => {});
      }
      return;
    }

    let refreshing = false;

    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      globalDeferredPrompt = e as DeferredPrompt;
      console.log('[PWA] Install prompt ready');
      notifyInstallListeners(true);
    };

    const handleAppInstalled = () => {
      globalDeferredPrompt = null;
      notifyInstallListeners(false);
      console.log('[PWA] App installed');
      // Marca esta IP como instalada para que admin no reciba más notificaciones de "nueva IP".
      fetch('/api/download/installed', { method: 'POST' }).catch(() => {});
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    navigator.serviceWorker
      .getRegistrations()
      .then(async (registrations) => {
        const hasLegacyRegistration = registrations.some((registration) => {
          const activeScript = registration.active?.scriptURL || registration.waiting?.scriptURL || registration.installing?.scriptURL || '';
          return activeScript.includes('/sw.js') && !activeScript.includes(`v=${encodeURIComponent(BUILD_VERSION)}`);
        });

        if (hasLegacyRegistration) {
          await Promise.all(registrations.map((registration) => registration.unregister()));
          await clearManagedCaches();
        }

        return navigator.serviceWorker.register(SERVICE_WORKER_URL, { updateViaCache: 'none' });
      })
      .then((registration) => {
        console.log('[PWA] SW registered:', registration.scope);
        registration.update().catch(() => {});
        requestNotificationPermission(registration);

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('[PWA] New version available');
                // Despachamos evento a la UI en lugar de forzar recarga.
                window.dispatchEvent(new CustomEvent('pwa-update-available', {
                  detail: {
                    update: () => newWorker.postMessage({ type: 'SKIP_WAITING' })
                  }
                }));
              }
            });
          }
        });
      })
      .catch((error) => {
        console.error('[PWA] SW registration failed:', error);
      });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  return null;
}

